import { buffers, type Channel } from 'redux-saga';
import { SIGN_OUT } from 'store/auth/actions';
import { createRequestProgressChannel } from 'service/upload';
import {
  getDocument,
  getImage,
  getPhoto,
  getWebFileObjectUrl,
  isImageContentType,
  revokeObjectUrls,
} from 'service/file';
import {
  actionChannel,
  all,
  call,
  put,
  race,
  SagaGenerator,
  select,
  take,
  takeEvery,
} from 'typed-redux-saga';
import { isError } from 'lib/isError';
import {
  addFileUpload,
  completeFileUpload,
  doLegacyFileUpload,
  eventLegacyFileUploaded,
  failFileUpload,
  fileUploadRemoved,
  removeFileUpload,
  setFileUploadPreview,
  setFileUploadTarget,
  startFileUpload,
  updateFileUploadProgress,
} from './actions';
import { selectFileUpload, selectFileUploads } from './selectors';
import type { FileUpload, FileUploadSource } from './types';
import {
  ALERT,
  ALERT_ERROR,
  CATCH_ERROR,
  doSetSettingSavePhotosToMediaLibrary,
  HIDE_BLOCKING_LOADER,
  OPEN_NATIVE_APP_SETTINGS,
  SHOW_BLOCKING_LOADER,
} from 'store/actions';
import { hasCameraPermission } from 'service/permission';
import type { FileBeingAdded } from 'service/file/types';
import { selectSettingSavePhotosToMediaLibrary } from 'store/app/selectors';
import { sagaConfirm } from 'store/sagaHelpers';
import { apiPost, checkInternetConnection } from 'service/api';
import { UPLOAD_FILE_TO_PROPERTY } from 'service/api/constants';
import { File as ExpoFile } from 'expo-file-system';
import type { File as OfflineAssetFile } from 'store/assets/types';

const isBrowserFile = (file: FileUploadSource): file is File =>
  typeof File !== 'undefined' && file instanceof File;

function* createFileUploadPreview(
  action: ReturnType<typeof addFileUpload>,
): Generator {
  const { contentType, file, filename, totalBytes, uploadId } = action.payload;
  if (!isImageContentType(contentType)) {
    return;
  }

  const upload = yield* select(selectFileUpload, uploadId);
  if (!upload || upload.file !== file || upload.previewUri) {
    return;
  }

  const shouldRevokePreview = isBrowserFile(file);
  const previewUri = shouldRevokePreview
    ? yield* call(getWebFileObjectUrl, {
        file,
        uri: undefined,
        name: filename,
        size: totalBytes,
        width: 0,
        height: 0,
        typeGroup: null,
      })
    : file.uri;
  if (!previewUri) {
    return;
  }

  const uploadAwaitingPreview = yield* select(selectFileUpload, uploadId);
  if (
    !uploadAwaitingPreview ||
    uploadAwaitingPreview.file !== file ||
    uploadAwaitingPreview.previewUri
  ) {
    if (shouldRevokePreview) {
      yield* call(revokeObjectUrls, [previewUri]);
    }
    return;
  }

  yield* put(setFileUploadPreview({ uploadId, file, previewUri }));
  const uploadWithPreview = yield* select(selectFileUpload, uploadId);
  if (shouldRevokePreview && uploadWithPreview?.previewUri !== previewUri) {
    yield* call(revokeObjectUrls, [previewUri]);
  }
}

function* removeFileUploadEntry(
  action: ReturnType<typeof removeFileUpload>,
): Generator {
  const upload = yield* select(selectFileUpload, action.payload.uploadId);
  if (!upload) {
    return;
  }

  yield* put(fileUploadRemoved({ uploadId: upload.uploadId }));
  if (isBrowserFile(upload.file) && upload.previewUri) {
    yield* call(revokeObjectUrls, [upload.previewUri]);
  }
}

function* removeAllFileUploads(): Generator {
  const uploads = yield* select(selectFileUploads);
  yield* all(
    uploads.map((upload) =>
      put(removeFileUpload({ uploadId: upload.uploadId })),
    ),
  );
}

function* waitForFileUploadRemoval(uploadId: string): Generator {
  while (true) {
    const action = yield* take(removeFileUpload);
    if (action.payload.uploadId === uploadId) {
      return;
    }
  }
}

function* transferFileUpload(upload: FileUpload): Generator {
  if (!upload.target) {
    throw new Error('Unable to upload file');
  }

  const uploadChannel = createRequestProgressChannel({
    abortErrorMessage: 'File upload was cancelled',
    body: upload.file,
    errorMessage: 'Unable to upload file',
    headers: {
      'Content-Type': upload.contentType,
      ...upload.target.headers,
    },
    method: upload.target.method,
    total: upload.totalBytes,
    url: upload.target.url,
  });

  try {
    while (true) {
      const event = yield* take(uploadChannel);
      if (event.type === 'progress') {
        yield* put(
          updateFileUploadProgress({
            uploadId: upload.uploadId,
            uploadedBytes: event.loaded,
            totalBytes: event.total,
          }),
        );
        continue;
      }
      if (event.type === 'done') {
        return;
      }
      throw event.error;
    }
  } finally {
    uploadChannel.close();
  }
}

function* fileUploadWorker(
  queue: Channel<ReturnType<typeof setFileUploadTarget>>,
): Generator {
  while (true) {
    const action = yield* take(queue);
    const upload = yield* select(selectFileUpload, action.payload.uploadId);
    if (!upload || upload.status !== 'queued') {
      continue;
    }

    yield* put(startFileUpload({ uploadId: upload.uploadId }));
    try {
      const result = yield* race({
        completed: call(transferFileUpload, upload),
        removed: call(waitForFileUploadRemoval, upload.uploadId),
      });
      if ('completed' in result) {
        yield* put(completeFileUpload({ uploadId: upload.uploadId }));
      }
    } catch (error) {
      yield* put(
        failFileUpload({
          uploadId: upload.uploadId,
          error: isError(error) ? error.message : 'Unable to upload file',
        }),
      );
    }
  }
}

function* watchFileUploadQueue(): Generator {
  const queue = yield* actionChannel(setFileUploadTarget, buffers.expanding());
  yield* all([
    call(fileUploadWorker, queue),
    call(fileUploadWorker, queue),
    call(fileUploadWorker, queue),
  ]);
}

function* legacyGetCameraImage(): SagaGenerator<FileBeingAdded | undefined> {
  const permission = yield* call(hasCameraPermission);
  if (!permission) {
    yield* put(
      ALERT({
        text: 'To take photos, please give RiskBase access to your camera in settings',
        altButton: {
          text: 'Open Settings',
          action: OPEN_NATIVE_APP_SETTINGS(),
        },
      }),
    );
    throw new Error(
      'To take photos, please give RiskBase access to your camera in settings',
    );
  }

  // Ask about saving to library
  let saveToMediaLibrary = false;
  const rawSaveToMediaLibrary = yield* select(
    selectSettingSavePhotosToMediaLibrary,
  );
  if (rawSaveToMediaLibrary === null) {
    const doBackups = yield* sagaConfirm({
      title: 'Save Photos to Media Library',
      text:
        'When taking photos, RiskBase can also save them to your ' +
        'device’s media library as a backup\n\n' +
        'This can be changed later in Settings',
      confirmText: 'Backup',
      cancelText: 'Don’t Backup',
    });
    yield* put(
      doSetSettingSavePhotosToMediaLibrary({
        enable: !!doBackups,
        ask: false,
      }),
    );
    saveToMediaLibrary = !!doBackups;
  } else {
    saveToMediaLibrary = rawSaveToMediaLibrary;
  }

  //
  try {
    const [file] = yield* call(getPhoto, {
      saveToMediaLibrary,
    });
    return file;
  } catch (error) {
    yield* put(
      ALERT_ERROR({
        error: new Error('Something went wrong taking the photo'),
      }),
    );
    yield* put(CATCH_ERROR({ error }));
    throw error;
  }
}

export const fileUploadSideEffects = [
  takeEvery(addFileUpload, createFileUploadPreview),
  takeEvery(removeFileUpload, removeFileUploadEntry),
  takeEvery(SIGN_OUT.success, removeAllFileUploads),
  call(watchFileUploadQueue),

  /**
   * Legacy (PHP)
   */
  takeEvery(doLegacyFileUpload, function* legacyFileUpload(action) {
    const { type, eventKey, propertyId, allowVideo } = action.payload;

    let file: FileBeingAdded | undefined;

    if (type === 'file-picker') {
      try {
        const files = yield* call(getDocument, { multiple: false });
        file = files[0];
      } catch (error) {
        if (isError(error)) {
          yield* put(ALERT_ERROR({ error }));
        }
      }
    } else if (type === 'image-picker') {
      try {
        const files = yield* call(getImage, { multiple: false, allowVideo });
        file = files[0];
      } catch (error) {
        if (isError(error)) {
          yield* put(ALERT_ERROR({ error }));
        }
      }
    } else if (type === 'camera') {
      try {
        file = yield* call(legacyGetCameraImage);
      } catch {}
    } else {
      yield* put(
        ALERT_ERROR({ error: new Error('Unknown type of file upload') }),
      );
    }

    if (!file) {
      return;
    }

    yield* put(SHOW_BLOCKING_LOADER());
    try {
      // Check internet connection and upload file
      yield* call(checkInternetConnection, {
        throwErrorIfOffline: true,
        forcePing: false,
        doPingIfWeb: true,
      });

      const formData = new FormData();
      formData.append('file', file.file ?? new ExpoFile(file.uri), file.name);
      formData.append('filename', file.name);

      const response = yield* call(
        apiPost<{ filesSyncFormat: Record<string, OfflineAssetFile> }>,
        UPLOAD_FILE_TO_PROPERTY(propertyId),
        formData,
        {
          skipConnectionCheck: true,
          multiformData: true,
        },
      );

      // Fire uploaded event
      yield* put(
        eventLegacyFileUploaded({
          eventKey: eventKey,
          propertyId,
          filesSyncFormat: response.filesSyncFormat,
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),
];
